  // ResultsPage.jsx
  import React, { useState, useEffect, useRef } from 'react';
  import { useSearchParams } from 'react-router-dom';
  import './PipsqueakWidget.css';
  import { useTheme } from "../ThemeContext";
  import { apifetch } from '../services/interceptors';
  import { shapeReport, categoryTally, plural } from './report/shapeReport';
  import StrengthsGaps from './report/StrengthsGaps';
  import { leadFieldError, leadFormErrors, LEAD_FIELDS } from './leadValidation';
  import { leadMagnetType } from '../v2/lib/leadMagnetTypes';
  import { generateEditorialPDF } from './report/editorialPdf';
  import ReportPage from './report/ReportPage';
  import ReportLogo from './report/ReportLogo';
  import appearanceTools from '../v2/lib/widgetAppearance';


  const CONFIG = {
    API_BASE: window.location.origin + '/s2/public',
    VERSION: '1.0.0'
  };

  const THEMES = {
    light: {
      primary: '#8b5cf6',
      primaryDark: '#7c3aed',
      primaryLight: '#a78bfa',
      secondary: '#06b6d4',
      success: '#10b981',
      warning: '#f59e0b',
      danger: '#ef4444',
      bg: '#ffffff',
      bgSecondary: '#f8fafc',
      text: '#0f172a',
      textLight: '#64748b',
      border: '#e2e8f0',
      accent: '#ec4899',
      surface: '#ffffff'
    },
    dark: {
      primary: '#a78bfa',
      primaryDark: '#8b5cf6',
      primaryLight: '#c4b5fd',
      secondary: '#22d3ee',
      success: '#34d399',
      warning: '#fbbf24',
      danger: '#f87171',
      bg: '#0f172a',
      bgSecondary: '#1e293b',
      text: '#f1f5f9',
      textLight: '#94a3b8',
      border: '#334155',
      accent: '#f472b6',
      surface: '#1e293b'
    },
    cyber: {
      primary: '#00ff88',
      primaryDark: '#00cc6a',
      primaryLight: '#33ff9f',
      secondary: '#7b61ff',
      success: '#00ff88',
      warning: '#ffcc00',
      danger: '#ff3366',
      bg: '#0a0a0f',
      bgSecondary: '#151522',
      text: '#ffffff',
      textLight: '#a0a0c0',
      border: '#2a2a3a',
      accent: '#ff00ff',
      surface: '#151522'
    }
  };

  // Can be used standalone on the /widget route (fetches its own data from the URL) OR
  // embedded by another page (DomainScanner, PublicScan) via props:
  //   embeddedResults  — a scan result object to render directly (skips the URL/fetch path)
  //   embeddedProvider — { name, website, address } branding
  //   embeddedTheme    — theme name for CSS variables
  //   forcedStyle      — 'slide_deck' | 'pdf' to override the tenant's report_style
  //   gated            — false to show the full report un-gated (internal tools)
  //   externalUnlock — when provided, the unlock CTA calls this instead of opening the
  //                    built-in lead modal (host page owns lead capture, e.g. PublicScan)
  const ResultsPage = ({ embeddedResults = null, embeddedProvider = null, embeddedTheme = null, forcedStyle = null, gated = null, externalUnlock = null } = {}) => {
    const [searchParams] = useSearchParams();

    const [state, setState] = useState({
      tenant:'target',
      loading: embeddedResults ? false : true,
      progress: 0,
      loadingMessage: 'Initializing scan...',
      error: null,
      results: embeddedResults || null,
      domain: embeddedResults?.domain || null,
      sessionId: null,
      widgetKey: null,
      theme: embeddedTheme || 'light',
      isBlurred: true,
      leadCollected: gated === false,
      showUnlockButton: false,
      provider: embeddedProvider || {
            name: null,
            website: "https://btsg.io",
            address: "506 7th Street, Wichita Falls, Texas 76301",
            logo: null,
      },
      config: { report_style: forcedStyle || undefined }
    });
            const [isExpanded, setIsExpanded] = React.useState(false);


    const [showUnlockModal, setShowUnlockModal] = useState(false);
    const [leadForm, setLeadForm] = useState({ firstName: '', lastName: '', phone: '', email: '', company: '' });
    // Per-field messages for the unlock form. A field shows its error once the visitor has
    // left it (or tried to submit), not while they are still typing the first character.
    const [leadErrors, setLeadErrors] = useState({});
    const [leadTouched, setLeadTouched] = useState({});
    const [leadSubmitting, setLeadSubmitting] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' });

    const unlockButtonRef = useRef(null);
    const config = state.config || {};

    // Root of the rendered report — theme/brand CSS variables are scoped to this element
    // so an embedded report never restyles the surrounding app.
    const reportRootRef = useRef(null);

    // Slide-deck mode: the report renders as full-page snap slides inside `.deck`.
    const deckRef = useRef(null);
    const [activeSlide, setActiveSlide] = useState(0);

    const fetchWidget = async (widgetKey) => {
      try {
        const res = await apifetch(`${CONFIG.API_BASE}/widget/${widgetKey}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const data = await res.json();
        // The widget's owner is no longer on a plan with Lead Magnets (see Public.js
        // WIDGET_UNAVAILABLE). Show the visitor the neutral notice, not an error.
        if (res.status === 403 && data?.code === 'feature_locked') {
          setState(prev => ({ ...prev, loading: false, unavailable: { title: data.error, message: data.message } }));
          return;
        }
        if (!res.ok) throw new Error(data.error || 'Could not load widget settings.');
        setState(prev => ({
          ...prev,
          widgetKey,
          theme: data.theme || 'light',
          config: data || {},
          // Tenant branding for the report cover/closing sections. The backend returns
          // logo URLs already routed through the public /s1/api/asset image proxy.
          provider: {
            name: data.tenant_name,
            domain: data.tenant_domain,
            website: data.website,
            address: data.address,
            logo: data.large_logo_url || data.logo_url || null,
            phone: data.contact_phone || null,
            email: data.contact_email || null,
            tagline: data.report_tagline || null,
            brandColors: Array.isArray(data.brand_colors) ? data.brand_colors : []
          }
        }));
        return data;
      } catch (err) {
        console.log(`Error getting widget config for widgetKey ${widgetKey}:`, err);
        setState(prev => ({
          ...prev,
          error: err.message,
          loading: false
        }));
      }
    };

    // Parse payload from URL on component mount (skipped when results are supplied via props)
    useEffect(() => {
      if (embeddedResults) return;
      const loadResults = async () => {
        try {
          const encodedData = searchParams.get('data');
          if (!encodedData) {
            throw new Error('No scan data provided');
          }

          const decodedData = decodeURIComponent(encodedData);
          const payload = JSON.parse(decodedData);

          setState(prev => ({
            ...prev,
            domain: payload.target,
            sessionId: payload.sessionId,
            widgetKey: payload.widgetKey,
            tenant:payload.tenant
          }));

          // The visitor's own addresses (Dark web and Email security widgets). The widget
          // puts them in the fragment so they never reach a server log; see openFullReport
          // in widget.bundle.js.
          const hashEmails = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('emails');
          const emails = hashEmails ? hashEmails.split(',').map(e => e.trim()).filter(Boolean) : [];

          const widget = await fetchWidget(payload.widgetKey);
          if (!widget) return;
          const company = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('company')?.trim().slice(0, 255) || '';
          setLeadForm(prev => ({ ...prev, company }));
          const args = { domain: payload.target, sessionId: payload.sessionId, widgetKey: payload.widgetKey, tenant: payload.tenant, emails, test: payload.test === true };
          await fetchScanResults(args.domain, args.sessionId, args.widgetKey, args.tenant, args.emails, args.test, company);
        } catch (error) {
          console.error('Failed to load results:', error);
          setState(prev => ({
            ...prev,
            loading: false,
            error: error.message || 'Failed to load scan results'
          }));
        }
      };

      loadResults();
    }, [searchParams]);

    // Embedded mode: keep local state in sync when the host supplies/updates results.
    useEffect(() => {
      if (!embeddedResults) return;
      setState(prev => ({
        ...prev,
        loading: false,
        results: embeddedResults,
        domain: embeddedResults.domain || prev.domain,
        theme: embeddedTheme || prev.theme,
        leadCollected: gated === false ? true : prev.leadCollected,
        provider: embeddedProvider || prev.provider,
        config: { ...(prev.config || {}), report_style: forcedStyle || prev.config?.report_style }
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embeddedResults]);

    // Check unlock button visibility on scroll
    useEffect(() => {
      const checkVisibility = () => {
        if (!state.isBlurred || state.leadCollected || !state.results) {
          setState(prev => ({ ...prev, showUnlockButton: false }));
          return;
        }

        const blurredCards = document.querySelectorAll('.blurred-card');
        if (!blurredCards.length) {
          setState(prev => ({ ...prev, showUnlockButton: false }));
          return;
        }

        let shouldShow = false;
        blurredCards.forEach(card => {
          const rect = card.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
          if (isVisible) shouldShow = true;
        });

        setState(prev => ({ ...prev, showUnlockButton: shouldShow }));
      };

      // The deck scrolls internally (scroll-snap), so listen on it; keep the window
      // listener as a fallback for non-deck layouts.
      const deckEl = deckRef.current;
      checkVisibility();
      if (deckEl) deckEl.addEventListener('scroll', checkVisibility);
      window.addEventListener('scroll', checkVisibility);
      return () => {
        if (deckEl) deckEl.removeEventListener('scroll', checkVisibility);
        window.removeEventListener('scroll', checkVisibility);
      };
    }, [state.isBlurred, state.leadCollected, state.results]);

    // Deck: reveal slide content as it enters view and track the active dot.
    useEffect(() => {
      if (state.loading || !state.results) return;
      const deck = deckRef.current;
      if (!deck) return;
      const slides = Array.from(deck.querySelectorAll('[data-slide]'));
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            setActiveSlide(slides.indexOf(entry.target));
          }
        });
      }, { root: deck, threshold: 0.45 });
      slides.forEach(s => observer.observe(s));
      // Fallback for environments where IntersectionObserver is throttled or unavailable:
      // mark the slide nearest the viewport center on every scroll.
      const markVisible = () => {
        const dh = deck.clientHeight;
        slides.forEach((s, idx) => {
          const r = s.getBoundingClientRect();
          if (r.top < dh * 0.55 && r.bottom > dh * 0.45) {
            s.classList.add('in-view');
            setActiveSlide(idx);
          }
        });
      };
      markVisible();
      deck.addEventListener('scroll', markVisible, { passive: true });
      return () => {
        observer.disconnect();
        deck.removeEventListener('scroll', markVisible);
      };
    }, [state.loading, state.results]);

    // ── Deck slide navigation ──────────────────────────────────────────
    // One shared animator with a lock: interval-driven (not rAF/native smooth —
    // rAF gets throttled in embedded contexts and native smooth scrolling gets
    // cancelled by React re-renders). The lock plus a short cooldown absorbs
    // trackpad momentum so one gesture moves exactly one slide.
    const deckAnimRef = useRef({ timer: null, lockUntil: 0 });
    const animateDeckTo = (target, duration = 650) => {
      const deck = deckRef.current;
      if (!deck) return;
      const anim = deckAnimRef.current;
      if (anim.timer) clearInterval(anim.timer);
      const start = deck.scrollTop;
      if (Math.abs(target - start) < 2) return;
      const t0 = performance.now();
      anim.lockUntil = t0 + duration + 250;
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      anim.timer = setInterval(() => {
        const p = Math.min(1, (performance.now() - t0) / duration);
        deck.scrollTop = start + (target - start) * ease(p);
        if (p >= 1) { clearInterval(anim.timer); anim.timer = null; }
      }, 16);
    };
    const deckLocked = () => performance.now() < deckAnimRef.current.lockUntil;

    const getSlides = () => {
      const deck = deckRef.current;
      return deck ? Array.from(deck.querySelectorAll('[data-slide]')) : [];
    };
    // The slide whose top edge is closest to the viewport top.
    const currentSlideIndex = () => {
      let best = 0, bestDist = Infinity;
      getSlides().forEach((s, i) => {
        const d = Math.abs(s.getBoundingClientRect().top);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    };

    const scrollToSlide = (i) => {
      const slides = getSlides();
      if (slides[i]) animateDeckTo(slides[i].offsetTop);
    };

    // Advance one step. Slides taller than the viewport are paged through in
    // ~85%-viewport increments before moving to the next/previous slide, so no
    // content is ever skipped.
    const stepSlide = (dir) => {
      const deck = deckRef.current;
      if (!deck || deckLocked()) return;
      const slides = getSlides();
      if (!slides.length) return;
      const idx = currentSlideIndex();
      const cur = slides[idx];
      const vh = deck.clientHeight || window.innerHeight || cur.offsetHeight;
      const r = cur.getBoundingClientRect();
      if (dir > 0) {
        if (r.bottom > vh + 10) {
          animateDeckTo(Math.min(deck.scrollTop + vh * 0.85, cur.offsetTop + cur.offsetHeight - vh), 450);
        } else if (idx < slides.length - 1) {
          animateDeckTo(slides[idx + 1].offsetTop);
        }
      } else {
        if (r.top < -10) {
          animateDeckTo(Math.max(deck.scrollTop - vh * 0.85, cur.offsetTop), 450);
        } else if (idx > 0) {
          animateDeckTo(slides[idx - 1].offsetTop);
        }
      }
    };

    // Wheel + keyboard navigation between slides. Slides have no inner scrollboxes —
    // long content is paginated across additional slides instead — but this also
    // lets any remaining scrollable element (e.g. a wide table) scroll natively
    // until it hits its edge. Touch devices keep native scrolling with CSS snap.
    useEffect(() => {
      if (state.loading || !state.results) return;
      const deck = deckRef.current;
      if (!deck) return;
      const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

      // Can an element between `el` and the deck still scroll in this direction?
      const innerCanScroll = (el, dy) => {
        let node = el;
        while (node && node !== deck) {
          if (node.scrollHeight > node.clientHeight + 1) {
            const canDown = node.scrollTop + node.clientHeight < node.scrollHeight - 1;
            const canUp = node.scrollTop > 0;
            if ((dy > 0 && canDown) || (dy < 0 && canUp)) return true;
          }
          node = node.parentElement;
        }
        return false;
      };

      const onWheel = (e) => {
        if (coarse) return;
        if (innerCanScroll(e.target, e.deltaY)) return; // let the panel scroll
        e.preventDefault();
        if (deckLocked() || Math.abs(e.deltaY) < 8) return;
        stepSlide(e.deltaY > 0 ? 1 : -1);
      };

      const onKey = (e) => {
        const tag = (e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (['ArrowDown', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); stepSlide(1); }
        else if (['ArrowUp', 'PageUp'].includes(e.key)) { e.preventDefault(); stepSlide(-1); }
        else if (e.key === 'Home') { e.preventDefault(); scrollToSlide(0); }
        else if (e.key === 'End') { e.preventDefault(); scrollToSlide(getSlides().length - 1); }
      };

      deck.addEventListener('wheel', onWheel, { passive: false });
      window.addEventListener('keydown', onKey);
      return () => {
        deck.removeEventListener('wheel', onWheel);
        window.removeEventListener('keydown', onKey);
        const anim = deckAnimRef.current;
        if (anim.timer) clearInterval(anim.timer);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.loading, state.results]);

    // Apply theme CSS variables, then layer the tenant's brand colors on top.
    //
    // These are scoped to the report's own root element (not documentElement) — the deck is
    // embedded inside the internal dashboard, and writing them globally would repaint the
    // whole app with the report theme / the tenant's brand palette.
    useEffect(() => {
      const el = reportRootRef.current;
      if (!el) return;
      const theme = THEMES[state.theme] || THEMES.light;

      const setVar = (key, val) => {
        el.style.setProperty(`--${key}`, val);
        // textLight -> --text-light as well: the stylesheet asks for both spellings.
        const kebab = key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
        if (kebab !== key) el.style.setProperty(`--${kebab}`, val);
        if (key === 'primary') {
          const rgb = hexToRgb(val);
          if (rgb) el.style.setProperty('--primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
        }
      };

      Object.entries(theme).forEach(([key, val]) => setVar(key, val));

      // Tenant brand colors: [0] Primary, [1] Secondary, [2] Accent (same order the tenant
      // Admin Panel's palette editor uses). Invalid entries are ignored.
      const isHex = (c) => typeof c === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(c.trim());
      const brand = (state.provider?.brandColors || []).filter(isHex).map(c => c.trim());
      if (brand[0]) {
        setVar('primary', brand[0]);
        setVar('primaryDark', shadeHex(brand[0], -0.18));
        setVar('primaryLight', shadeHex(brand[0], 0.25));
      }
      if (brand[1]) setVar('secondary', brand[1]);
      if (brand[2]) setVar('accent', brand[2]);
      const appearance = appearanceTools.normalize(state.config?.appearance);
      if (appearance) {
        const a = { ...appearanceTools.defaults, ...appearance };
        setVar('primary', a.buttonBackground);
        setVar('primaryDark', shadeHex(a.buttonBackground, -0.18));
        setVar('primaryLight', shadeHex(a.buttonBackground, 0.25));
        el.style.fontFamily = appearanceTools.fonts[a.font];
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.theme, state.provider?.brandColors, state.loading, state.results, state.config?.appearance]);

    // The active palette as inline custom properties, for anything rendered OUTSIDE the
    // report root the theme effect writes to — the unlock modal and the toast are siblings
    // of <ReportPage>, so every var(--surface) / var(--text) in their CSS resolved to
    // nothing there and the copy fell back to whatever colour it inherited.
    //
    // Both spellings are emitted. The effect writes the THEMES keys verbatim, so compound
    // names land as --textLight and --bgSecondary, while PipsqueakWidget.css asks for
    // --text-light and --bg-secondary in 22 places. Those references have never resolved.
    const paletteStyle = () => {
      const theme = THEMES[state.theme] || THEMES.light;
      const brand = brandPrimary(theme);
      const rgb = hexToRgb(brand);
      const appearance = appearanceTools.normalize(state.config?.appearance);
      const a = appearance ? { ...appearanceTools.defaults, ...appearance } : null;
      return {
        ...(a ? { fontFamily: appearanceTools.fonts[a.font], '--primary': a.buttonBackground,
          '--surface': a.background, '--text': a.textColor } : {}),
        '--primary': a ? a.buttonBackground : brand,
        '--primaryDark': theme.primaryDark,
        '--primary-dark': theme.primaryDark,
        '--primaryLight': theme.primaryLight,
        '--primary-light': theme.primaryLight,
        '--primary-rgb': rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : undefined,
        '--secondary': theme.secondary,
        '--success': theme.success,
        '--warning': theme.warning,
        '--danger': theme.danger,
        '--bg': theme.bg,
        '--bgSecondary': theme.bgSecondary,
        '--bg-secondary': theme.bgSecondary,
        '--surface': a ? a.background : theme.surface,
        '--border': theme.border,
        '--text': a ? a.textColor : theme.text,
        '--textLight': theme.textLight,
        '--text-light': theme.textLight,
        '--accent': theme.accent,
      };
    };

    // The tenant's brand primary, for the places that paint before the CSS variables are
    // mounted (the loading screen). Same [0] = Primary convention the effect above uses.
    const brandPrimary = (theme) => {
      const c = (state.provider?.brandColors || [])[0];
      return typeof c === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(c.trim())
        ? c.trim()
        : theme.primary;
    };

    const hexToRgb = (hex) => {
      // Expand 3-digit shorthand (#abc) so brand colors in either form work.
      const full = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
      const normalized = full ? `#${full[1]}${full[1]}${full[2]}${full[2]}${full[3]}${full[3]}` : hex;
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalized);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };

    // Lighten (amount > 0) or darken (amount < 0) a hex color — used to derive the
    // primaryDark/primaryLight shades from a tenant's single brand primary.
    const shadeHex = (hex, amount) => {
      const rgb = hexToRgb(hex);
      if (!rgb) return hex;
      const adj = (c) => {
        const v = amount >= 0 ? c + (255 - c) * amount : c * (1 + amount);
        return Math.max(0, Math.min(255, Math.round(v)));
      };
      return `#${[adj(rgb.r), adj(rgb.g), adj(rgb.b)].map(c => c.toString(16).padStart(2, '0')).join('')}`;
    };

    // Fetch scan results
    const fetchScanResults = async (domain, sessionId, widgetKey, tenant, emails = [], test = false, company = '') => {
      let progressInterval;
      try {
        // Simulate progress while fetching
        const messages = [
          `Connecting to ${tenant} network.`,
          'Analyzing DNS records...',
          'Checking SSL/TLS certificates...',
          'Scanning security headers...',
          'Analyzing email security...',
          'Checking infrastructure...',
          'Detecting typosquatting domains...',
          'Finalizing security report...'
        ];

        progressInterval = setInterval(() => {
          setState(prev => {
            if (prev.progress >= 98) return prev;
            // Slower, more consistent increments
            const nextProgress = prev.progress + (Math.random() * 2 + 0.5);
            const msgIndex = Math.min(Math.floor((nextProgress / 100) * messages.length), messages.length - 1);
            return {
              ...prev,
              progress: Math.min(nextProgress, 98),
              loadingMessage: messages[msgIndex]
            };
          });
        }, 700);

        const response = await apifetch(`${CONFIG.API_BASE}/domain-scan`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-ID': sessionId,
            'X-Client-Version': CONFIG.VERSION
          },
          body: JSON.stringify({
            target: domain,
            targetType: 'domain',
            sessionId: sessionId,
            widgetKey: widgetKey,
            company,
            // A test run from Saved widgets: scanned as normal, but not counted as use.
            test: test || undefined,
            // Optional addresses to check; the server ignores them on types that do not ask.
            emails: emails.length ? emails : undefined,
            // No `modules`, deliberately. The server derives them from the widget's lead
            // magnet type, and an explicit array on the request OVERRIDES that type
            // (routes/Public.js). Sending all seven here meant every widget ran the full
            // attack-surface scan whatever it was configured as: an Email security report
            // returned every category, and every scan paid for typosquatting — 42s of a
            // ~50s scan — when the type it was built as needed one module and a second.
            timestamp: new Date().toISOString()
          })
        });

        clearInterval(progressInterval);

        if (!response.ok) {
          // Checked again at scan time: a page opened just before the owner's plan lapsed
          // still ends on the notice rather than on "Failed to Load Results".
          if (response.status === 403) {
            const body = await response.json().catch(() => ({}));
            if (body?.code === 'feature_locked') {
              setState(prev => ({ ...prev, loading: false, unavailable: { title: body.error, message: body.message } }));
              return;
            }
          }
          if (response.status === 404) {
            clearInterval(progressInterval);
  setState(prev => ({
    ...prev,
    loading: false,
    error: `Scan failed: ${response.status} - ${response.statusText || 'No scan results found'}`
  }));
            return;
          }
          throw new Error(`Failed to fetch results: ${response.status}`);
        }

        const results = await response.json();

        setState(prev => ({
          ...prev,
          progress: 100,
          loadingMessage: 'Analysis Complete!'
        }));

        await new Promise(r => setTimeout(r, 800));

        setState(prev => ({
          ...prev,
          results,
          loading: false,
          error: null,
          isBlurred: true,
          leadCollected: false
        }));
      } catch (err) {
        if (progressInterval) clearInterval(progressInterval);
        console.error('Scan failed:', err);
  setState(prev => ({
    ...prev,
    loading: false,
    error: err.message || 'Failed to complete security scan. Please try again.'
  }));
      }
    };

    // Mock data
    const setMockData = (domain) => {
      const mockResults = {
        riskScore: 65,
        domain: domain,
        categories: [
          {
            name: 'SSL/TLS Certificate',
            icon: '🔒',
            findings: [
              { severity: 'critical', title: 'Certificate Expires in 30 Days', description: 'SSL certificate expires on 2024-12-30. Renew immediately to prevent service interruption.' },
              { severity: 'medium', title: 'Weak Cipher Suites', description: 'Server supports TLS 1.0 and 1.1 which are deprecated. Enable TLS 1.2 and 1.3 only.' },
              { severity: 'low', title: 'HSTS Not Enabled', description: 'HTTP Strict Transport Security is not configured. Enable HSTS to enforce HTTPS connections.' }
            ]
          },
          {
            name: 'Security Headers',
            icon: '🛡️',
            findings: [
              { severity: 'critical', title: 'Missing X-Frame-Options', description: 'Clickjacking protection not detected. Add X-Frame-Options: DENY or SAMEORIGIN header.' },
              { severity: 'critical', title: 'Missing CSP', description: 'Content Security Policy not implemented. Add CSP header to prevent XSS attacks.' },
              { severity: 'high', title: 'X-Content-Type-Options', description: 'Nosniff header not set. Add X-Content-Type-Options: nosniff to prevent MIME sniffing.' }
            ]
          },
          {
            name: 'DNS Configuration',
            icon: '🌐',
            findings: [
              { severity: 'high', title: 'DNSSEC Not Enabled', description: 'Domain lacks DNSSEC protection. Enable DNSSEC to prevent DNS spoofing attacks.' },
              { severity: 'medium', title: 'SPF Record Missing', description: 'Email authentication not configured. Add SPF record to prevent email spoofing.' },
              { severity: 'medium', title: 'DMARC Policy Not Set', description: 'No DMARC record found. Implement DMARC to protect against email phishing.' }
            ]
          },
          {
            name: 'Email Security',
            icon: '📧',
            findings: [
              { severity: 'high', title: 'No DKIM Signature', description: 'Email signing not configured. Implement DKIM to verify email authenticity.' },
              { severity: 'medium', title: 'Missing DMARC', description: 'No email authentication policy. Add DMARC record with p=quarantine or p=reject.' },
              { severity: 'low', title: 'SPF Softfail', description: 'SPF policy is not strict. Use -all instead of ~all for hard fail.' }
            ]
          },
          {
            name: 'Infrastructure',
            icon: '⚙️',
            findings: [
              { severity: 'critical', title: 'Open Ports Detected', description: 'Ports 21 (FTP), 23 (Telnet), 3389 (RDP) are exposed. Close unnecessary ports immediately.' },
              { severity: 'high', title: 'Outdated Software', description: 'Server software versions are outdated. Update to latest stable versions for security patches.' },
              { severity: 'medium', title: 'No Rate Limiting', description: 'No brute force protection detected. Implement rate limiting on login endpoints.' }
            ]
          }
        ]
      };

      setState(prev => ({
        ...prev,
        loading: false,
        results: mockResults,
        error: null,
        isBlurred: true,
        leadCollected: false
      }));
    };

    // Helper functions
    const getRiskLevel = (score) => {
      if (score >= 75) return 'low';
      if (score >= 60) return 'medium';
      if (score >= 40) return 'high';
      return 'critical';
    };

    const getGrade = (score) => {
      if (score >= 90) return 'A+';
      if (score >= 80) return 'A';
      if (score >= 75) return 'B+';
      if (score >= 70) return 'B';
      if (score >= 60) return 'C';
      if (score >= 40) return 'D';
      return 'F';
    };

    // Show toast
    const showToast = (message, type = 'info') => {
      setToast({ show: true, message, type });
      setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
    };

    const updateLeadField = (field, value) => {
      setLeadForm(prev => ({ ...prev, [field]: value }));
      // Once a field has been flagged, re-check it as they type so the message clears the
      // moment it is fixed.
      if (leadTouched[field]) {
        setLeadErrors(prev => ({ ...prev, [field]: leadFieldError(field, value) }));
      }
    };

    const touchLeadField = (field) => {
      setLeadTouched(prev => ({ ...prev, [field]: true }));
      setLeadErrors(prev => ({ ...prev, [field]: leadFieldError(field, leadForm[field]) }));
    };

    // Handle lead submission.
    //
    // Validated here and again on the server, which also checks the email's domain can
    // receive mail. The report unlocks only once the server has accepted the lead: the
    // save used to be fire-and-forget, so a rejected or failed save unlocked it anyway and
    // the tenant never got the lead.
    const handleLeadSubmit = async (e) => {
      e.preventDefault();
      if (leadSubmitting) return;

      const errors = leadFormErrors(leadForm);
      setLeadTouched(Object.fromEntries(LEAD_FIELDS.map(f => [f, true])));
      setLeadErrors(errors);
      if (Object.keys(errors).length) {
        const first = LEAD_FIELDS.find(f => errors[f]);
        document.getElementById(`lead-${first}`)?.focus();
        return;
      }

      setLeadSubmitting(true);
      try {
        if (state.widgetKey) {
          const { firstName, lastName, phone, email, company } = leadForm;
          const response = await apifetch(`${CONFIG.API_BASE}/domain-scan`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Widget-Key': state.widgetKey
            },
            body: JSON.stringify({
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              // Still sent joined, for a server that predates the split fields.
              name: `${firstName.trim()} ${lastName.trim()}`,
              phone,
              email,
              company,
              target: state.domain,
              targetType: 'domain',
              widgetKey: state.widgetKey,
              save: true,
              providedResults: state.results,
              domain: state.domain,
              sessionId: state.sessionId,
              timestamp: new Date().toISOString()
            })
          });

          if (!response.ok) {
            let body = {};
            try { body = await response.json(); } catch { /* not JSON */ }
            if (response.status === 400 && body.fields) {
              setLeadErrors(body.fields);
              const first = LEAD_FIELDS.find(f => body.fields[f]);
              if (first) document.getElementById(`lead-${first}`)?.focus();
              return;
            }
            throw new Error(body.error || `Request failed (${response.status})`);
          }
        }

        setState(prev => ({
          ...prev,
          leadCollected: true,
          isBlurred: false,
          showUnlockButton: false
        }));

        setShowUnlockModal(false);
        showToast('Thank you! Your report is now unlocked.', 'success');

      } catch (error) {
        console.error('Lead capture failed:', error);
        showToast("We couldn't save your details. Please try again.", 'error');
      } finally {
        setLeadSubmitting(false);
      }
    };

    // Download PDF report
    // The report download.
    //
    // This used to carry its own ~1,000-line copy of the old PDF generator: the same
    // layout, pagination and gradient styling as components/pdfGenerator.jsx, duplicated.
    // So restyling the report in one place changed the prospecting download and left the
    // lead magnet — the copy visitors actually receive — on the old design.
    //
    // One generator now, in report/editorialPdf.js. The theme is deliberately not passed:
    // the document has a single print palette, and a visitor viewing the page in dark mode
    // should still be handed a report that prints.
    const downloadReport = async () => {
      if (!state.results || !state.domain) {
        showToast('No report available', 'error');
        return;
      }

      showToast('Generating security report...', 'info');

      try {
        await generateEditorialPDF(state.results, state.domain, {
          // The tenant whose widget produced this scan, so the report is titled for the
          // business the visitor recognises rather than their own bare domain.
          prospectCompany: state.provider?.name || state.config?.tenant_name || '',
          // So the file a visitor keeps is titled after the report they asked for.
          reportType: state.config?.reportType,
          saveFile: true,
        });
        showToast('Security report generated successfully', 'success');
      } catch (err) {
        console.error('PDF export failed:', err);
        showToast('Failed to generate PDF: ' + err.message, 'error');
      }
    };


    // loadPDFLibraries lived here for the inline generator that has gone; report/editorialPdf.js
    // loads html2canvas and jsPDF itself, so nothing on this page needs to.


    // Render professional progress loader.
    //
    // Painted from the theme object rather than through CSS variables. The variables are
    // written onto reportRootRef, which does not exist until results render, so while
    // this screen is up nothing defines --bg, --bg-gradient or --text: each of those
    // declarations was invalid, leaving the card transparent (white) and the copy
    // inheriting the host page's own text colour — light grey on white, unreadable.
    //
    // The card's translucent white overlays were written for a dark theme too, so they
    // only apply when the theme actually is dark.
    const renderSkeletonLoader = () => {
      const theme = THEMES[state.theme] || THEMES.light;
      const isDark = state.theme === 'dark' || state.theme === 'cyber';

      return (
      <div className="results-page bg-light" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        background: theme.bg,
        color: theme.text
      }}>
        <div className="loading-content" style={{
          textAlign: 'center',
          maxWidth: '500px',
          width: '90%',
          padding: '60px 40px',
          borderRadius: '32px',
          background: isDark ? 'rgba(255, 255, 255, 0.03)' : theme.surface,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.border}`,
          boxShadow: isDark ? '0 24px 48px rgba(0,0,0,0.4)' : '0 24px 48px rgba(15,23,42,0.08)',
          animation: 'loader-fade-in 0.8s ease-out'
        }}>
          <div className="loader-icon" style={{ marginBottom: '32px', position: 'relative' }}>
            <div className="pulse-ring" style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '140px',
              height: '140px',
              borderRadius: '50%',
              border: `2px solid ${brandPrimary(theme)}`,
              opacity: '0.3',
              animation: 'loader-pulse 2s infinite'
            }}></div>
            <div style={{
              background: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.bgSecondary,
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.border}`,
              backdropFilter: 'blur(5px)'
            }}>
              <svg viewBox="0 0 24 24" width="40" height="40" stroke={brandPrimary(theme)} fill="none" strokeWidth="1.5" style={{ filter: `drop-shadow(0 0 12px ${brandPrimary(theme)})` }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M12 8v4l3 3" opacity="0.6" />
              </svg>
            </div>
          </div>

          <h2 style={{
            fontSize: '28px',
            fontWeight: '800',
            marginBottom: '12px',
            letterSpacing: '-1px',
            WebkitBackgroundClip: 'text',
            color: theme.text,
          }}>
            Generating report
          </h2>

          <p style={{
            // --text-muted is defined by no stylesheet this page loads; it only ever
            // existed inside .built-in-reports-modal.
            fontSize: '15px',
            color: theme.textLight,
            marginBottom: '40px',
            height: '24px',
            transition: 'all 0.3s ease',
            letterSpacing: '0.5px'
          }}>
            {state.loadingMessage}
          </p>

          <div className="progress-wrapper" style={{ position: 'relative', marginBottom: '20px' }}>
            <div style={{
              height: '12px',
              background: isDark ? 'rgba(255,255,255,0.05)' : theme.bgSecondary,
              borderRadius: '100px',
              overflow: 'hidden',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : theme.border}`,
              boxShadow: isDark ? 'inset 0 2px 4px rgba(0,0,0,0.2)' : 'inset 0 1px 2px rgba(15,23,42,0.06)'
            }}>
              <div style={{
                width: `${state.progress}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${brandPrimary(theme)}, ${theme.secondary})`,
                boxShadow: `0 0 20px ${brandPrimary(theme)}`,
                borderRadius: '100px',
                transition: 'width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
              }}></div>
            </div>
          </div>

          <div style={{
            fontSize: '48px',
            fontWeight: '900',
            color: theme.text,
            fontVariantNumeric: 'tabular-nums',
            textShadow: isDark ? '0 0 20px rgba(255,255,255,0.2)' : 'none'
          }}>
            {Math.round(state.progress)}<span style={{ fontSize: '20px', opacity: 0.5, color: theme.text }}>%</span>
          </div>
        </div>

        <style>{`
          @keyframes loader-pulse {
            0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.5; }
            100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
          }
          @keyframes loader-fade-in {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
      );
    };

    // Render results with blur effect
    // Opening the lead capture: either the host's own flow, or our built-in modal.
    const triggerUnlock = () => { if (externalUnlock) externalUnlock(); else setShowUnlockModal(true); };

    // One unlock-form field: the input, and its message underneath once it has one.
    const renderLeadField = (field, placeholder, { type = 'text', autoComplete, inputMode } = {}) => {
      const error = leadErrors[field];
      return (
        <div className="form-group">
          <input
            id={`lead-${field}`}
            type={type}
            className={`modal-form-input${error ? ' is-invalid' : ''}`}
            placeholder={field === 'company' ? `${placeholder} (optional)` : placeholder}
            aria-label={placeholder}
            autoComplete={autoComplete}
            inputMode={inputMode}
            value={leadForm[field]}
            onChange={(e) => updateLeadField(field, e.target.value)}
            onBlur={() => touchLeadField(field)}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? `lead-${field}-error` : undefined}
            required={field !== 'company'}
          />
          {error && <div className="form-error" id={`lead-${field}-error`} role="alert">{error}</div>}
        </div>
      );
    };

    // Unlock modal + toast are shared by both the deck and the single-page layouts.
    // When the host supplies its own capture (externalUnlock), suppress our modal.
    const renderUnlockModal = () => (
      !externalUnlock && showUnlockModal && (
        <>
          <div className="unlock-modal-overlay" onClick={() => setShowUnlockModal(false)}></div>
          <div className="unlock-modal widget-custom-unlock" style={paletteStyle()}>
              {state.config?.appearance && <style>{(() => {
                const a = { ...appearanceTools.defaults, ...appearanceTools.normalize(state.config.appearance) };
                return `.widget-custom-unlock .modal-form-input { background:${a.inputTransparent ? 'transparent' : a.inputBackground}; color:${a.inputText}; border:1px solid ${a.borderColor}; border-radius:${a.inputRadius}px; min-height:${a.inputHeight}px; font-family:inherit; }
                  .widget-custom-unlock .modal-form-input::placeholder { color:${a.placeholderColor}; }
                  .widget-custom-unlock .modal-form-input:focus { outline:2px solid ${a.focusColor}; outline-offset:2px; }
                  .widget-custom-unlock .unlock-modal-btn { background:${a.buttonBackground}; color:${a.buttonText}; border-radius:${a.buttonRadius}px; font-family:inherit; }`;
              })()}</style>}
            <div className="unlock-modal-content">
              <button className="unlock-modal-close" onClick={() => setShowUnlockModal(false)}>&times;</button>
              <div className="unlock-modal-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="unlock-modal-title">Unlock Full Report</h3>
              <p className="unlock-modal-subtitle">Enter your details to access the complete security analysis</p>
              {/* noValidate: our messages, under each field, instead of the browser's bubbles. */}
              <form className="unlock-modal-form" onSubmit={handleLeadSubmit} noValidate>
                <div className="form-row">
                  {renderLeadField('firstName', 'First Name', { autoComplete: 'given-name' })}
                  {renderLeadField('lastName', 'Last Name', { autoComplete: 'family-name' })}
                </div>
                {renderLeadField('email', 'Work Email', { type: 'email', autoComplete: 'email', inputMode: 'email' })}
                {renderLeadField('phone', 'Phone Number', { type: 'tel', autoComplete: 'tel', inputMode: 'tel' })}
                {renderLeadField('company', 'Company Name', { autoComplete: 'organization' })}
                <button type="submit" className="unlock-modal-btn" disabled={leadSubmitting}>
                  <span>{leadSubmitting ? 'Checking…' : 'Unlock Now'}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M13 7l5 5m0 0l-5 5m5-5H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
              </form>
              <p className="unlock-modal-note">Your information is secure and never shared</p>
            </div>
          </div>
        </>
      )
    );
    const renderToast = () => (
      toast.show && (
        <div className={`toast toast-${toast.type} show`} style={paletteStyle()}>
          <div className="toast-icon">{toast.type === 'success' ? '✓' : toast.type === 'error' ? '✕' : 'ℹ'}</div>
          <div className="toast-message">{toast.message}</div>
        </div>
      )
    );

    const renderResults = () => {
      if (!state.results) return null;

      // Effective presentation style + gating. Deck is forced for embedded internal tools;
      // customer-facing surfaces default to the tenant's report_style ('pdf').
      const reportStyle = forcedStyle || state.config?.report_style || 'pdf';
      const effectiveGated = gated !== null ? gated : true;
      const showGate = effectiveGated && !state.leadCollected;

      // Single-page (PDF) layout — shares the deck's data + visuals.
      if (reportStyle !== 'slide_deck') {
        // The widget config carries the lead magnet this scan was run as, so the deck and
        // the PDF name themselves after the report the visitor was actually promised.
        const shaped = shapeReport(state.results, state.domain, state.provider?.name,
                                   state.config?.reportType);
        return (
          <>
            <ReportPage
              rootRef={reportRootRef}
              shaped={shaped}
              provider={state.provider}
              gated={effectiveGated}
              leadCollected={state.leadCollected}
              onUnlock={triggerUnlock}
              onDownload={downloadReport}
              version={CONFIG.VERSION}
            />
            {renderUnlockModal()}
            {renderToast()}
          </>
        );
      }

      const data = state.results;
      const totalFindings = data.categories?.reduce((sum, cat) => sum + (cat.findings?.length || 0), 0) || 0;
      const criticalCount = data.categories?.reduce((sum, cat) => sum + (cat.findings?.filter(f => f.status !== 'pass' && f.severity === 'critical')?.length || 0), 0) || 0;
      const highCount = data.categories?.reduce((sum, cat) => sum + (cat.findings?.filter(f => f.status !== 'pass' && f.severity === 'high')?.length || 0), 0) || 0;
      const mediumCount = data.categories?.reduce((sum, cat) => sum + (cat.findings?.filter(f => f.status !== 'pass' && f.severity === 'medium')?.length || 0), 0) || 0;
      const lowCount = data.categories?.reduce((sum, cat) => sum + (cat.findings?.filter(f => f.status !== 'pass' && f.severity === 'low')?.length || 0), 0) || 0;

      // Calculate Security Score: weighted average health (Critical=0, High=25, Medium=50, Low=100)
      const totalHealthPoints = (criticalCount * 0) + (highCount * 25) + (mediumCount * 50) + (lowCount * 100);
      const riskLevel = getRiskLevel(data.riskScore);

      const providerName = state.provider?.name;
      // What this document calls itself. The deck builds its own numbers rather than going
      // through shapeReport, so the framing is looked up here from the same table.
      const reportSubtitle = leadMagnetType(state.config?.reportType).reportTitle;
      const categoryCount = data.categories?.length || 0;

      // ── Deck data shaping ──────────────────────────────────────────────
      const sevOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
      const allFindings = (data.categories || []).flatMap(cat =>
        (cat.findings || []).map(f => ({ ...f, categoryName: cat.name })));
      // Issues table: non-passing findings, most severe first.
      const issueRows = allFindings
        .filter(f => f.status !== 'pass')
        .sort((a, b) => (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9))
        .slice(0, 8);
      // The full good-and-bad lists for the Strengths & Gaps slide.
      const strengths = allFindings.filter(f => f.status === 'pass');
      const gaps = allFindings
        .filter(f => f.status === 'fail' || f.status === 'warning')
        .sort((a, b) => (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9));
      const passCount = strengths.length;
      const issueCount = allFindings.filter(f => f.status !== 'pass').length;
      // Per-category letter grade from its worst unresolved finding.
      const categoryGrade = (cat) => {
        const fs = (cat.findings || []).filter(f => f.status !== 'pass');
        if (fs.some(f => f.severity === 'critical')) return 'F';
        if (fs.some(f => f.severity === 'high')) return 'D';
        if (fs.some(f => f.severity === 'medium')) return 'C';
        if (fs.length > 0) return 'B';
        return 'A';
      };
      const sevPill = (severity) => (
        <span className={`deck-pill sev-${severity || 'info'}`}>{(severity || 'info').toUpperCase()}</span>
      );

      // Split a category's findings across as many slides as needed so a slide never
      // needs an inner scrollbar. Heuristic weight per finding (title/description/detail
      // length) — a single very long finding gets its own slide rather than overflowing.
      const CHARS_PER_CATEGORY_SLIDE = 700;
      const paginateFindings = (findings) => {
        if (!findings.length) return [[]];
        const pages = [];
        let current = [];
        let weight = 0;
        findings.forEach((f) => {
          const findingWeight = 160 + (f.description?.length || 0) + (f.detail?.length || 0) * 0.9;
          if (current.length && weight + findingWeight > CHARS_PER_CATEGORY_SLIDE) {
            pages.push(current);
            current = [];
            weight = 0;
          }
          current.push(f);
          weight += findingWeight;
        });
        if (current.length) pages.push(current);
        return pages;
      };

      const HEADER_KEYWORDS = [
        'What we tested', 'Test Results', 'Analysis', 'Risk Assessment', 'Security Risks',
        'Why this matters', 'Key Findings', 'Security Benefits', 'Recommended Actions',
        'Recommendations', 'Compliance', 'Compliance Implications', 'Impact Assessment',
        'Threat Analysis', 'Attack Vector Analysis', 'Security Considerations', 'Detailed Findings',
        'Policy Analysis', 'Security Implications', 'What makes these domains threats',
        'Defensive Recommendations', 'Threat Intelligence', 'What are parked/registration pages',
        'Why this is good', 'Why this is excellent', 'Still Recommended Actions', 'Best Practices',
        'Immediate Recommended Actions', 'Medium-Term Actions', 'Long-Term Strategy',
        'Additional Recommendations', 'Implementation Steps', 'Verification', 'Immediate Fix',
        'Alternative Approaches', 'Priority', 'Security Assessment', 'Critical Risks',
        'Specific Threat Analysis', 'Infrastructure Detection', 'Content Analysis', 'Threat Assessment',
        'Security Analysis', "What's wrong", 'Why this is a concern', 'Why this is a critical failure',
        'Why this is a significant security gap', 'What this means', 'Important Note', 'Analysis by Algorithm'
      ];

      // Static (non-scrolling) rendering of a finding's detail text — same parsing rules
      // used across the report so the deck and the single-page report read identically.
      const renderFindingDetail = (detail) => {
        const fmt = (text) => text
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/🔒/g, '<span class="detail-lock-icon">🔒</span>');
        return detail.split('\n').map((line, i) => {
          if (line.trim() === '') return <div key={i} className="detail-spacer" />;
          const trimmed = line.trim();
          // A keyword on its own line is a section header. With a sentence after the colon it
          // is a label and its text — styled as a header, the whole paragraph rendered bold.
          const headerKey = HEADER_KEYWORDS.find(k => trimmed.startsWith(k + ':'));
          if (headerKey && trimmed.length === headerKey.length + 1) {
            return <div key={i} className="detail-header" dangerouslySetInnerHTML={{ __html: fmt(line) }} />;
          }
          // The bullet dot comes from CSS, so the source's own "- " is dropped; a numbered step
          // keeps its number and gets no dot. Both were shown before: "• - item", "• 1. step".
          const numbered = trimmed.match(/^(\d+)\.\s+(.*)$/);
          if (numbered) {
            return <div key={i} className="detail-bullet detail-numbered" data-n={`${numbered[1]}.`} dangerouslySetInnerHTML={{ __html: fmt(numbered[2]) }} />;
          }
          if (trimmed.match(/^[-•*]\s/)) {
            return <div key={i} className="detail-bullet" dangerouslySetInnerHTML={{ __html: fmt(trimmed.replace(/^[-•*]\s+/, '')) }} />;
          }
          if (line.includes(':') && !line.match(/^https?:\/\//)) {
            const parts = line.split(':');
            if (parts.length >= 2 && parts[0].trim().length < 50) {
              return (
                <div key={i} className="detail-label-line">
                  <span className="detail-label">{parts[0].trim()}:</span>
                  <span className="detail-label-value" dangerouslySetInnerHTML={{ __html: fmt(parts.slice(1).join(':').trim()) }} />
                </div>
              );
            }
          }
          return <div key={i} className="detail-text-line" dangerouslySetInnerHTML={{ __html: fmt(line) }} />;
        });
      };

      // Translate a technical finding into business-impact copy for the narrative slides.
      const impactCopy = (f) => {
        const t = `${f.title} ${f.categoryName || ''}`.toLowerCase();
        if (t.includes('dmarc') || t.includes('spf') || t.includes('spoof')) return {
          icon: '📧', head: 'Email Domain Impersonation',
          body: `Weak email authentication means attackers can send mail that appears to come from ${state.domain} — targeting your customers, partners, or payment recipients with convincing fraud.`
        };
        if (t.includes('dkim')) return {
          icon: '✉️', head: 'Email Integrity Unverified',
          body: `Without DKIM signing, recipients have no cryptographic proof your email is genuine — invoices, contracts, and client communications are easier to forge.`
        };
        if (t.includes('typo') || t.includes('lookalike') || t.includes('squat')) return {
          icon: '🛡️', head: 'Lookalike Domains — Active Threat',
          body: `Lookalike domains can be registered by bad actors to launch spear-phishing campaigns that impersonate ${state.domain} to your customers and staff.`
        };
        if (t.includes('breach') || t.includes('credential') || t.includes('dark web')) return {
          icon: '🔑', head: 'Exposed Credentials = Live Risk',
          body: `Credentials tied to your domain circulating in breach data are in attacker toolkits right now — a direct path into your systems without MFA enforcement.`
        };
        if (t.includes('header') || t.includes('hsts') || t.includes('cookie') || t.includes('csp')) return {
          icon: '🌐', head: 'Website Hardening Gaps',
          body: `Missing web security protections leave ${state.domain} more exposed to interception and cross-site attacks — fixable in hours, but visible to every attacker scanning your site.`
        };
        if (t.includes('ssl') || t.includes('tls') || t.includes('certificate') || t.includes('encrypt')) return {
          icon: '🔒', head: 'Encryption Weaknesses',
          body: `Outdated or misconfigured encryption undermines the confidentiality of every session with your site and can trigger browser trust warnings for your visitors.`
        };
        if (t.includes('dnssec') || t.includes('dns') || t.includes('nameserver')) return {
          icon: '🗺️', head: 'DNS Trust Gaps',
          body: `Without DNS-level protections, responses for ${state.domain} are easier to tamper with — enabling traffic redirection your users would never notice.`
        };
        return {
          icon: '⚠️', head: f.title,
          body: `${f.description || 'This gap is visible from the outside.'} Left unresolved, this is exactly the kind of weakness attackers actively scan for.`
        };
      };
      const impactCards = [];
      const seenImpacts = new Set();
      for (const f of issueRows) {
        const c = impactCopy(f);
        if (seenImpacts.has(c.head)) continue;
        seenImpacts.add(c.head);
        impactCards.push(c);
        if (impactCards.length >= 4) break;
      }

      // Pull a named section (e.g. "Recommended Actions") out of a finding's detail text.
      const extractSection = (detail, keys) => {
        if (!detail) return null;
        const lines = detail.split('\n');
        for (let i = 0; i < lines.length; i++) {
          const l = lines[i].trim().toLowerCase();
          if (keys.some(k => l.startsWith(k))) {
            const inline = lines[i].split(':').slice(1).join(':').replace(/\*\*/g, '').trim();
            if (inline) return inline;
            const out = [];
            for (let j = i + 1; j < lines.length && out.length < 2; j++) {
              const s = lines[j].trim();
              if (!s) { if (out.length) break; continue; }
              if (/^([-•*]|\d+\.)\s/.test(s)) out.push(s.replace(/^([-•*]|\d+\.)\s*/, '').replace(/\*\*/g, ''));
              else { out.push(s.replace(/\*\*/g, '')); break; }
            }
            return out.length ? out.join('; ') : null;
          }
        }
        return null;
      };
      const fixFor = (f) => extractSection(f.detail, ['recommended actions', 'immediate recommended', 'immediate fix', 'recommendations', 'still recommended actions'])
        || 'Remediate this finding following current security best practices, then re-scan to verify.';
      const whyFor = (f) => extractSection(f.detail, ['why this matters', 'why this is a', 'why this is'])
        || f.description || 'Reduces your external attack surface.';
      const challengeRows = issueRows.slice(0, 5);

      // Slide count: title, context, snapshot, high-risk table [, impact][, fixes],
      // categories, outcome, final.
      const totalSlides = 5 + (showGate ? 1 : 0) + (impactCards.length ? 1 : 0) + (challengeRows.length ? 1 : 0) + categoryCount + 2;

      return (
        <div className="results-page deck-mode" ref={reportRootRef}>
          <div className={`deck ${showGate ? 'deck-gated' : ''}`} ref={deckRef}>

            {/* ── Slide 1: Title ─────────────────────────────────────────── */}
            <section className="deck-slide deck-title-slide" data-slide>
              <div className="deck-slide-inner deck-center">
                <ReportLogo provider={state.provider} className="reveal" />
                {providerName && <div className="deck-presenter-name reveal d1">{providerName}</div>}
                <div className="deck-title-divider reveal d1"></div>
                <h1 className="deck-main-title reveal d2">{state.domain}</h1>
                <p className="deck-subtitle reveal d3">{reportSubtitle}</p>
                <p className="deck-presented-by reveal d3">
                  {providerName ? `Presented by ${providerName}` : 'Powered by Pipsqueak NetScan'}
                </p>
                <div className="deck-scroll-hint">Scroll to begin<span className="deck-scroll-arrow">▾</span></div>
              </div>
            </section>

            {/* ── Slide 2: Scan context ──────────────────────────────────── */}
            <section className="deck-slide" data-slide>
              <div className="deck-slide-inner">
                <div className="deck-eyebrow reveal">🔎 CONTEXT</div>
                <h2 className="deck-heading reveal d1">
                  We looked at {state.domain} the way an attacker would
                </h2>
                <p className="deck-lead reveal d2">
                  This is an external surface scan of the public-facing infrastructure of {state.domain} —
                  no credentials, nothing internal was touched. Everything in this report is what an
                  outsider can already see today.
                </p>
                <div className="deck-grid-4">
                  <div className="deck-context-card reveal d1">
                    <div className="deck-context-icon">🌐</div>
                    <h4>Public-Facing Risks</h4>
                    <p>Exposed services, open ports, and misconfigured systems visible from the outside.</p>
                  </div>
                  <div className="deck-context-card reveal d2">
                    <div className="deck-context-icon">🗺️</div>
                    <h4>Infrastructure Exposure</h4>
                    <p>Domains, DNS records, and web properties tied to {state.domain}.</p>
                  </div>
                  <div className="deck-context-card reveal d3">
                    <div className="deck-context-icon">🔑</div>
                    <h4>Credential Leaks</h4>
                    <p>Breach databases searched for exposed accounts linked to this domain.</p>
                  </div>
                  <div className="deck-context-card reveal d4">
                    <div className="deck-context-icon">📋</div>
                    <h4>Compliance Posture</h4>
                    <p>Email security, encryption, and DNS configuration measured against best practice.</p>
                  </div>
                </div>
                <p className="deck-meta reveal d4">
                  🌐 {state.domain} · Scanned {new Date().toLocaleDateString()} · {plural(categoryCount, 'category', 'categories')} · {plural(totalFindings, 'check')}
                </p>
              </div>
            </section>

            {/* ── Slide 3: Executive risk snapshot ───────────────────────── */}
            <section className="deck-slide" data-slide>
              <div className="deck-slide-inner">
                <div className="deck-eyebrow reveal">📊 EXTERNAL SCAN RESULTS</div>
                <h2 className="deck-heading reveal d1">Executive Risk Snapshot</h2>

                {/* Score and the 2x2 severity tiles side by side, exposure full width beneath. */}
                <div className="deck-snapshot-stack">
                  <div className="deck-panel deck-gauge-card reveal d1">
                    <div className="deck-panel-label">SECURITY SCORE</div>
                    <svg viewBox="0 0 200 118" className="deck-gauge" aria-hidden="true">
                      <defs>
                        <linearGradient id="deckGaugeGrad" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#ef4444" />
                          <stop offset="55%" stopColor="#f59e0b" />
                          <stop offset="100%" stopColor="#10b981" />
                        </linearGradient>
                      </defs>
                      <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none"
                        stroke="url(#deckGaugeGrad)" strokeWidth="14" strokeLinecap="round" />
                      <g transform={`rotate(${(Math.max(0, Math.min(100, data.riskScore)) / 100) * 180 - 90} 100 100)`}>
                        <line x1="100" y1="100" x2="100" y2="36" stroke="var(--text)" strokeWidth="3" strokeLinecap="round" />
                        <circle cx="100" cy="100" r="6" fill="var(--text)" />
                      </g>
                      <text x="20" y="115" className="deck-gauge-tick">0</text>
                      <text x="180" y="115" textAnchor="end" className="deck-gauge-tick">100</text>
                    </svg>
                    <div className={`deck-gauge-value ${riskLevel}`}>{data.riskScore}<span>/100</span></div>
                    <div className={`risk-badge ${riskLevel}`}>{riskLevel.toUpperCase()} RISK</div>
                  </div>
                  <div className="deck-sev-tiles">
                    <div className="deck-panel deck-sev-tile reveal d1"><div className="deck-sev-num sev-critical-text">{criticalCount}</div><div className="deck-sev-name">CRITICAL</div></div>
                    <div className="deck-panel deck-sev-tile reveal d2"><div className="deck-sev-num sev-high-text">{highCount}</div><div className="deck-sev-name">HIGH</div></div>
                    <div className="deck-panel deck-sev-tile reveal d3"><div className="deck-sev-num sev-medium-text">{mediumCount}</div><div className="deck-sev-name">MEDIUM</div></div>
                    <div className="deck-panel deck-sev-tile reveal d4"><div className="deck-sev-num sev-low-text">{lowCount}</div><div className="deck-sev-name">LOW</div></div>
                  </div>
                  <div className="deck-panel deck-exposure-card reveal d2">
                    <div className="deck-panel-label">EXTERNAL SURFACE EXPOSURE</div>
                    <div className="deck-exposure-list">
                      {data.categories?.map((cat, i) => {
                        const g = categoryGrade(cat);
                        return (
                          <div key={i} className="deck-exposure-row">
                            <span className={`deck-grade grade-${g}`}>{g}</span>
                            <span className="deck-exposure-name">{cat.name}</span>
                            <span className="deck-exposure-count">{categoryTally(cat)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className={`deck-callout reveal d3 ${riskLevel}`}>
                  {criticalCount + highCount > 0
                    ? <>A <strong>{getGrade(data.riskScore)}</strong> grade with <strong>{criticalCount + highCount} critical/high finding{criticalCount + highCount === 1 ? '' : 's'}</strong> means attackers have a working path in — these gaps are visible to anyone who looks.</>
                    : issueCount > 0
                      ? <>A <strong>{getGrade(data.riskScore)}</strong> grade with no critical or high findings — {plural(passCount, 'check')} passed, and {plural(issueCount, 'smaller item')} below {issueCount === 1 ? 'is a hardening opportunity' : 'are hardening opportunities'}.</>
                      : <>A <strong>{getGrade(data.riskScore)}</strong> grade — all {plural(passCount, 'check')} passed. Here is what we verified.</>}
                </div>
              </div>
            </section>

            {/* ── Strengths & gaps: the good and the bad, in full ───────── */}
            <section className="deck-slide" data-slide>
              <div className="deck-slide-inner">
                <div className="deck-eyebrow reveal">⚖️ STRENGTHS &amp; GAPS</div>
                <h2 className="deck-heading reveal d1">What Needs Attention <span className="deck-heading-accent">&amp; What's Working</span></h2>
                <StrengthsGaps strengths={strengths} gaps={gaps} revealClass="reveal" />
              </div>
            </section>

            {/* ── Unlock gate slide: shown right after the Executive Risk Snapshot; every
                   slide after it is blurred until the visitor unlocks (see .deck-gated CSS). ── */}
            {showGate && (
              <section className="deck-slide deck-unlock-slide" data-slide>
                <div className="deck-slide-inner deck-center">
                  <div className="report-unlock-card">
                    <div className="report-unlock-icon">🔒</div>
                    <h3>Unlock the Full Report</h3>
                    <p>You've seen the executive snapshot. Enter your details to reveal every finding, the business-impact breakdown, and the recommended fixes.</p>
                    <button className="report-unlock-btn" onClick={triggerUnlock}>Unlock Full Report</button>
                  </div>
                </div>
              </section>
            )}

            {/* ── Slide 4: High-risk areas table ─────────────────────────── */}
            <section className="deck-slide" data-slide>
              <div className="deck-slide-inner">
                <div className="deck-eyebrow reveal">🚩 FINDINGS DETAIL</div>
                <h2 className="deck-heading reveal d1">High-Risk Areas <span className="deck-heading-accent">Identified</span></h2>
                {issueRows.length === 0 ? (
                  <div className="deck-panel deck-clean-panel reveal d1">
                    <strong>No high-risk areas identified.</strong>
                    <span>Every check in this report passed. The strengths above are what keeps {state.domain} in good shape.</span>
                  </div>
                ) : (
                  <div className="deck-panel deck-table-panel reveal d1">
                    <table className="deck-table">
                      <thead>
                        <tr><th>Severity</th><th>Finding</th><th>Category</th></tr>
                      </thead>
                      <tbody>
                        {issueRows.map((f, i) => (
                          <tr key={i}>
                            <td>{sevPill(f.severity)}</td>
                            <td>
                              <div className="deck-table-title">{f.title}</div>
                              {f.description && <div className="deck-table-desc">{f.description}</div>}
                            </td>
                            <td className="deck-table-cat">{f.categoryName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>

            {/* ── Slide 5: Business risk translation ─────────────────────── */}
            {impactCards.length > 0 && (
              <section className="deck-slide" data-slide>
                <div className="deck-slide-inner">
                  <div className="deck-eyebrow reveal">📉 BUSINESS RISK TRANSLATION</div>
                  <h2 className="deck-heading reveal d1">Operational Impact for <span className="deck-heading-accent">{state.domain}</span></h2>
                  <div className="deck-impact-grid">
                    {impactCards.map((c, i) => (
                      <div key={i} className={`deck-panel deck-impact-card reveal d${Math.min(4, i + 1)}`}>
                        <div className="deck-impact-icon">{c.icon}</div>
                        <h4>{c.head}</h4>
                        <p>{c.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* ── Slide 6: Challenges → recommended fixes ────────────────── */}
            {challengeRows.length > 0 && (
              <section className="deck-slide" data-slide>
                <div className="deck-slide-inner">
                  <div className="deck-eyebrow reveal">🧭 AT A GLANCE</div>
                  <h2 className="deck-heading reveal d1">Findings <span className="deck-heading-accent">→ Recommended Fixes</span></h2>
                  <div className="deck-panel deck-table-panel reveal d2">
                    <table className="deck-table deck-fix-table">
                      <thead>
                        <tr><th>Challenge</th><th>Recommended Fix</th><th>Why It Matters</th></tr>
                      </thead>
                      <tbody>
                        {challengeRows.map((f, i) => (
                          <tr key={i}>
                            <td>
                              <div className="deck-table-title">{f.title}</div>
                              <div className="deck-table-desc">{sevPill(f.severity)}</div>
                            </td>
                            <td className="deck-fix-cell">{fixFor(f)}</td>
                            <td className="deck-fix-cell">{whyFor(f)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

{/* ── Category slides: findings are paginated across as many slides as a
     category needs, so nothing inside a slide has to be clicked into view
     or scrolled — long content simply continues on the next slide. ── */}
    {data.categories?.flatMap((category, index) => {
      const catFindings = category.findings || [];
      const catPass = catFindings.filter(f => f.status === 'pass').length;
      const catWarn = catFindings.filter(f => f.status === 'warning').length;
      const catFail = catFindings.filter(f => f.status === 'fail').length;
      const catGrade = categoryGrade(category);
      const pages = paginateFindings(catFindings);

      return pages.map((pageFindings, pageIndex) => (
        <section key={`${index}-${pageIndex}`} className="deck-slide deck-category-slide" data-slide>
          <div className="deck-slide-inner">
            <div className="deck-eyebrow reveal">
              🚩 CATEGORY {index + 1} / {categoryCount}
              {pages.length > 1 ? ` · PART ${pageIndex + 1}/${pages.length}` : ''}
            </div>
            <h2 className="deck-heading reveal d1">{category.name}</h2>
            {pageIndex === 0 && (
              <div className="deck-cat-stats reveal d1">
                <span className={`deck-grade grade-${catGrade}`}>{catGrade}</span>
                <span className="deck-cat-chip">{catFindings.length} finding{catFindings.length === 1 ? '' : 's'}</span>
                {catPass > 0 && <span className="deck-cat-chip chip-pass">✓ {catPass} passed</span>}
                {catWarn > 0 && <span className="deck-cat-chip chip-warn">⚠ {catWarn} warning{catWarn === 1 ? '' : 's'}</span>}
                {catFail > 0 && <span className="deck-cat-chip chip-fail">✗ {catFail} failed</span>}
              </div>
            )}
            <div className="deck-cat-findings reveal d2">
              {pageFindings.length === 0 && (
                <div className="deck-detail-empty">No findings in this category.</div>
              )}
              {pageFindings.map((finding, fi) => (
                <div key={fi} className="deck-panel deck-cat-finding">
                  <div className="deck-detail-header">
                    {sevPill(finding.severity)}
                    <h3 className="deck-detail-title">{finding.title}</h3>
                  </div>
                  {finding.description && (
                    <p className="deck-detail-desc">{finding.description}</p>
                  )}
                  <div className="finding-status d-flex flex-wrap gap-2 align-items-center mb-2">
                    <span className={`status-badge status-${finding.status}`}>
                      {finding.status === 'pass' ? '✅ Pass' :
                        finding.status === 'fail' ? '❌ Fail' :
                          finding.status === 'warning' ? '⚠️ Warning' : 'ℹ️ Info'}
                    </span>
                    {finding.value && (
                      <span className="finding-value badge bg-light text-dark">Value: {finding.value}</span>
                    )}
                    {finding.impact > 0 && (
                      <span className="finding-impact badge bg-danger">Impact: {finding.impact}%</span>
                    )}
                  </div>
                  {finding.detail && finding.detail.length > 0 && (
                    <div className="deck-cat-finding-detail">
                      <div className="deck-panel-label">DETAILED ANALYSIS</div>
                      <div className="detail-text mt-2">{renderFindingDetail(finding.detail)}</div>
                      {finding.compliance && finding.compliance.length > 0 && (
                        <div className="detail-compliance mt-3 pt-2 border-top">
                          <span className="compliance-label d-block mb-2 small fw-bold text-secondary">Compliance Frameworks:</span>
                          <div className="d-flex flex-wrap gap-1">
                            {finding.compliance.map((standard, stdIdx) => (
                              <span key={stdIdx} className="compliance-badge badge bg-info bg-opacity-10 text-info">
                                {standard}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      ));
    })}


            {/* ── Outcome slide: why fix this now ────────────────────────── */}
            <section className="deck-slide" data-slide>
              <div className="deck-slide-inner">
                <div className="deck-eyebrow reveal">🏆 THE OUTCOME</div>
                <h2 className="deck-heading reveal d1">
                  Why {state.domain} Would Benefit <span className="deck-heading-accent">From Fixing This Now</span>
                </h2>
                <div className="deck-outcome-grid">
                  <div className="deck-outcome-card reveal d1">
                    <div className="deck-outcome-num">01</div>
                    <h4>Close the Gaps Before Attackers Find Them</h4>
                    <p>
                      {criticalCount + highCount > 0
                        ? `${criticalCount + highCount} critical/high finding${criticalCount + highCount === 1 ? '' : 's'} in this report ${criticalCount + highCount === 1 ? 'is' : 'are'} visible to anyone who scans ${state.domain}. Fixing them removes the easiest attack paths first.`
                        : issueCount > 0
                          ? `No critical gaps today — closing the ${plural(issueCount, 'remaining item')} in this report keeps it that way.`
                          : `No gaps today. Continuous monitoring keeps it that way as new exposures appear.`}
                    </p>
                  </div>
                  <div className="deck-outcome-card reveal d2">
                    <div className="deck-outcome-num">02</div>
                    <h4>Protect Customers & Reputation</h4>
                    <p>Email impersonation and lookalike domains directly target the people who trust you. Closing those vectors protects your brand and every message you send.</p>
                  </div>
                  <div className="deck-outcome-card reveal d3">
                    <div className="deck-outcome-num">03</div>
                    <h4>Meet Compliance Expectations</h4>
                    <p>The findings here map to frameworks like SOC 2, ISO 27001, and OWASP. Remediating them strengthens your posture ahead of audits and customer security reviews.</p>
                  </div>
                  <div className="deck-outcome-card reveal d4">
                    <div className="deck-outcome-num">04</div>
                    <h4>Sleep Better at Night</h4>
                    <p>
                      {providerName
                        ? `With ${providerName} monitoring your external surface, new exposures get caught and remediated before they're weaponized — while you focus on running your business.`
                        : `Continuous monitoring of your external surface catches new exposures before they're weaponized — prevention costs far less than recovery.`}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* ── Final slide: thank you / next step ─────────────────────── */}
            <section className="deck-slide deck-final-slide" data-slide>
              <div className="deck-slide-inner deck-center">
                <ReportLogo provider={state.provider} className="reveal" />
                <h2 className="deck-main-title deck-final-title reveal d1">Thank You</h2>
                <p className="deck-lead deck-center-lead reveal d2">
                  These findings show {state.domain} from the outside-in.
                  {providerName ? ` ${providerName} can help you close these gaps before an attacker finds them.` :
                    ' Closing these gaps now costs far less than recovering from a breach later.'}
                </p>
                {state.provider?.tagline && (
                  <p className="deck-tagline reveal d2">{state.provider.tagline}</p>
                )}
                {state.leadCollected && (
                  <button className="download-btn deck-download reveal d3" onClick={downloadReport}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="18" height="18">
                      <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ fontWeight: '700', letterSpacing: '0.5px' }}>DOWNLOAD FULL REPORT</span>
                  </button>
                )}
                <div className="deck-contact reveal d3">
                  {state.provider?.website && (
                    <div className="deck-contact-line">🌍 <a href={state.provider.website} target="_blank" rel="noreferrer">{state.provider.website}</a></div>
                  )}
                  {state.provider?.phone && (
                    <div className="deck-contact-line">📞 <a href={`tel:${state.provider.phone}`}>{state.provider.phone}</a></div>
                  )}
                  {state.provider?.email && (
                    <div className="deck-contact-line">✉️ <a href={`mailto:${state.provider.email}`}>{state.provider.email}</a></div>
                  )}
                  {state.provider?.address && (
                    <div className="deck-contact-line">📍 {state.provider.address}</div>
                  )}
                </div>
                <div className="powered-by deck-powered">
                  <span>Powered by</span>
                  <strong>Pipsqueak NetScan</strong>
                  <span className="version">v{CONFIG.VERSION}</span>
                </div>
              </div>
            </section>
          </div>

          {/* Dot rail navigation */}
          <nav className="deck-dots" aria-label="Report sections">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                type="button"
                className={`deck-dot ${activeSlide === i ? 'active' : ''}`}
                onClick={() => scrollToSlide(i)}
                aria-label={`Go to section ${i + 1} of ${totalSlides}`}
              />
            ))}
          </nav>

          {/* Fixed Unlock Button — visible once the visitor reaches the snapshot/gate. */}
          {showGate && (
            <button
              ref={unlockButtonRef}
              className="fixed-unlock-btn"
              onClick={triggerUnlock}
              /* Show while on the snapshot and the locked slides, but not on the gate slide
                 itself (index 3) — its card already carries the CTA. */
              style={{ display: (activeSlide >= 2 && activeSlide !== 3) ? 'flex' : 'none' }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="24" height="24">
                <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Unlock Full Report</span>
            </button>
          )}

          {renderUnlockModal()}
          {renderToast()}
        </div>
      );
    };

    // The owner's plan no longer includes Lead Magnets. Deliberately not an error screen:
    // no retry button, nothing about plans — the visitor is not the customer.
    if (state.unavailable) {
      return (
        <div className="results-page">
          <div className="results-container full-width">
            <div className="error-state widget-unavailable">
              <div className="error-icon" aria-hidden="true">🕒</div>
              <h2 className="error-title">{state.unavailable.title || 'This lead magnet widget is currently unavailable.'}</h2>
              <p className="error-message">{state.unavailable.message || 'Please check back soon.'}</p>
            </div>
          </div>
        </div>
      );
    }

    // Render error state
    if (state.error) {
      return (
        <div className="results-page">
          <div className="results-container full-width">
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <h2 className="error-title">Failed to Load Results</h2>
              <p className="error-message">{state.error}</p>
              <button className="error-retry-btn" onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }

    return state.loading ? renderSkeletonLoader() : renderResults();
  };

  export default ResultsPage;